import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { ConditionOperatorEnum, type Condition } from '@repo/api-gw-sdk';
import type { Column } from '@tanstack/react-table';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useViewParameters } from '@/contexts/navigation';
import { useWorkspace } from '@/contexts/useWorkspace';
import {
  createCombineCondition,
  isCombineCondition,
  isValueCondition,
  type FilterProperty,
} from '@/types/advanceFilter';

import { FiltersPanelWrapper } from './filtersPanelWrapper';

import { GroupTitle } from '../option-menu/groupTitle';
import { getProperties } from '../queryBuilder/propertiesUtils';
import { PropertyGroups } from '../queryBuilder/propertyGroups';

export const FiltersPanel = ({
  columns,
  title,
  subtitle,
  expandAllByDefault,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: Column<any>[];
  title?: string;
  subtitle?: string;
  expandAllByDefault?: boolean;
}) => {
  const { leftPanel } = useWorkspace();
  const { query, setParams } = useViewParameters();

  return (
    <UnmanagedFiltersPanel
      expandAllByDefault={expandAllByDefault}
      columns={columns}
      filter={query}
      title={title}
      subtitle={subtitle}
      onClose={() => leftPanel.setIsOpen(false)}
      onFilterChange={(newFilter) =>
        setParams((state) => ({
          ...state,
          query: newFilter,
        }))
      }
    />
  );
};

export const UnmanagedFiltersPanel = ({
  columns,
  filter,
  onFilterChange,
  onClose,
  title,
  subtitle,
  expandAllByDefault,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: Column<any>[];
  filter: Condition | undefined;
  onFilterChange: (filter: Condition) => void;
  onClose?: () => void;
  title?: string;
  subtitle?: string;
  expandAllByDefault?: boolean;
}) => {
  const pathname = usePathname();
  const [drillDownParameters, setDrillDownParameters] = useState<{
    Component?: (props: { filter: Condition }) => React.ReactElement;
    title?: string;
    subtitle?: string;
  }>({});

  const combineCondition = isCombineCondition(filter)
    ? filter!
    : createCombineCondition();

  const groupedColumns = columns
    .filter((x) => x.getCanFilter())
    .reduce<Record<string, Column<unknown>[]>>((acc, column) => {
      getProperties(column).forEach((prop) => {
        acc[prop.group] = acc[prop.group] || [];
        if (!acc[prop.group].includes(column)) {
          acc[prop.group].push(column);
        }
      });
      return acc;
    }, {});

  const renderFiltersPanel = () => setDrillDownParameters({});

  const renderDrillDownFilter = (
    title: string,
    subtitle: string,
    Component: (props: { filter: Condition }) => React.ReactElement
  ) => {
    setDrillDownParameters({
      title,
      subtitle,
      Component,
    });
  };

  useEffect(() => {
    if (drillDownParameters.Component) {
      setDrillDownParameters({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <FiltersPanelWrapper
      title={
        drillDownParameters.Component
          ? drillDownParameters.title
          : title || 'Filter resources'
      }
      subtitle={
        drillDownParameters.Component
          ? drillDownParameters.subtitle
          : subtitle ||
            'Use filters to explore resources and manage your backups.'
      }
      onClose={onClose}
      onBack={drillDownParameters.Component ? renderFiltersPanel : undefined}
    >
      <Stack hidden={!!drillDownParameters.Component}>
        {[''].concat(Object.keys(PropertyGroups)).map((group) => {
          const columnsList = groupedColumns[group];
          if (!columnsList?.length) {
            return;
          }

          const title = PropertyGroups[group]?.title;
          return (
            <Box key={group}>
              {title && <GroupTitle title={title} />}
              {columnsList.map((column) => {
                const properties = getProperties(column);
                const propertyWithComponent = properties.find(
                  (property) =>
                    property?.filter?.Component ||
                    property?.filter?.fullPanelComponent?.Component
                );

                if (!propertyWithComponent) {
                  return null;
                }

                const fullPanelComponent =
                  propertyWithComponent?.filter?.fullPanelComponent;

                return (
                  <Accordion
                    defaultExpanded={
                      expandAllByDefault ||
                      propertyWithComponent.filter?.expandedByDefault
                    }
                    disableGutters
                    key={column.id}
                    sx={{
                      backgroundColor: 'transparent',
                    }}
                  >
                    <AccordionSummary
                      data-testid={`filter-accordion-${column.id}`}
                      className='px-[24px] py-[12px] font-semibold'
                      expandIcon={
                        <i className='material-symbols-arrow-right-rounded' />
                      }
                      sx={{
                        backgroundColor: 'transparent',
                        '& .MuiAccordionSummary-root': {
                          backgroundColor: 'transparent',
                        },
                        '.MuiAccordionSummary-expandIconWrapper.Mui-expanded':
                          fullPanelComponent
                            ? {
                                transform: 'none !important',
                              }
                            : {
                                transform: 'rotate(90deg)',
                              },
                      }}
                      onClick={
                        fullPanelComponent &&
                        (() =>
                          setDrillDownParameters({
                            title: fullPanelComponent.title,
                            subtitle: fullPanelComponent.subtitle,
                            Component: ({ filter }: { filter: Condition }) => (
                              <FilterComponent
                                property={propertyWithComponent}
                                filter={filter}
                                renderFiltersPanel={renderFiltersPanel}
                                renderDrillDownFilter={renderDrillDownFilter}
                                onChange={onFilterChange}
                              />
                            ),
                          }))
                      }
                    >
                      <Stack
                        className='w-full'
                        direction={'row'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                      >
                        <Typography variant='h4'>{`${column.columnDef.header}`}</Typography>
                        {combineCondition?.conditions?.some(
                          (x) =>
                            isValueCondition(x) &&
                            properties.some(
                              (property) =>
                                x.property === property?.name &&
                                x.operator !== ConditionOperatorEnum.NotIn
                            )
                        ) && (
                          <Box
                            className='rounded inline-block mr-[16px] h-[8px] w-[8px]'
                            sx={{
                              backgroundColor: 'currentColor',
                            }}
                          />
                        )}
                      </Stack>
                    </AccordionSummary>
                    {!fullPanelComponent && (
                      <AccordionDetails className='py-[12px] px-[24px]'>
                        <FilterComponent
                          property={propertyWithComponent}
                          filter={combineCondition}
                          renderFiltersPanel={renderFiltersPanel}
                          renderDrillDownFilter={renderDrillDownFilter}
                          onChange={onFilterChange}
                        />
                      </AccordionDetails>
                    )}
                  </Accordion>
                );
              })}
            </Box>
          );
        })}
      </Stack>
      {drillDownParameters.Component && (
        <drillDownParameters.Component filter={combineCondition} />
      )}
    </FiltersPanelWrapper>
  );
};

function FilterComponent({
  filter,
  property,
  onChange,
  renderFiltersPanel,
  renderDrillDownFilter,
}: {
  filter: Condition;
  property: FilterProperty | undefined;
  onChange: (filter: Condition) => void;
  renderFiltersPanel: () => void;
  renderDrillDownFilter: (
    title: string,
    subtitle: string,
    component: (props: { filter: Condition }) => React.ReactElement
  ) => void;
}) {
  if (!property) {
    return null;
  }

  const Component = property.filter?.Component;
  const FullPanelComponent = property.filter?.fullPanelComponent?.Component;

  if (Component) {
    return (
      <Component
        property={property}
        conditions={filter.conditions || []}
        data-testid={`filter-${property.name}`}
        renderDrillDownFilter={renderDrillDownFilter}
        onChange={(conditions) => {
          const newFilter = {
            ...filter,
            conditions,
          };

          onChange(newFilter);
        }}
      />
    );
  }

  if (FullPanelComponent) {
    return (
      <FullPanelComponent
        property={property}
        conditions={filter.conditions || []}
        data-testid={`filter-${property.name}`}
        onDoneClick={renderFiltersPanel}
        renderDrillDownFilter={renderDrillDownFilter}
        onChange={(conditions) => {
          const newFilter = {
            ...filter,
            conditions,
          };

          onChange(newFilter);
        }}
      />
    );
  }

  return null;
}
